/**
 * Presentational mappings from the eligibility detail payload to header UI.
 */

import type { EligibilityDetailResponse } from "../../data-model/eligibilityDetail";
import { formatDisplayDate } from "../../utils/displayDate";
import {
  formatEligibilityTierLabel,
  formatSnakeCaseEnumLabel,
} from "../../utils/stringModifications";
import type { DiagnosisSummary } from "./diagnosis/DiagnosisSummaryRow";
import { mapEligibilityDiagnoses } from "./mapEligibilityDiagnosis";

const EMPTY_VALUE = "—";

/** Field shown in the collapsible patient-strip grid. */
export interface EligibilityCollapsibleField {
  label: string;
  value: string;
}

/** Header values derived from `row` + `patient` on the detail payload. */
export interface EligibilityHeaderViewModel {
  initials: string;
  name: string;
  mrn: string;
  dobWithAge: string;
  sex: string;
  eligibilityStatus: string;
  eligibilityTier: string;
  screeningSummary: string;
  patientStatus: string;
  additionalStudies: string[];
  fhirIdentifier: string | null;
  collapsibleFields: EligibilityCollapsibleField[];
  diagnosis: {
    primary: DiagnosisSummary;
    additional: DiagnosisSummary[];
  };
}

/**
 * Read a string demographic field from the patient strip.
 *
 * @param demographics - Patient demographics map
 * @param key - Demographic key
 * @returns String value or empty string
 */
function demographicString(
  demographics: Record<string, unknown>,
  key: string,
): string {
  const value = demographics[key];
  return typeof value === "string" ? value : "";
}

/**
 * Read a numeric demographic field from the patient strip.
 *
 * @param demographics - Patient demographics map
 * @param key - Demographic key
 * @returns Number value or null
 */
function demographicNumber(
  demographics: Record<string, unknown>,
  key: string,
): number | null {
  const value = demographics[key];
  return typeof value === "number" ? value : null;
}

/**
 * Build up-to-two-letter initials from a display name.
 *
 * @param name - Patient display name
 * @returns Upper-case initials, or "?" when the name is empty
 */
export function patientInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

/**
 * Format a date of birth with an optional age suffix.
 *
 * @param birthDate - ISO birth date
 * @param age - Age in years when known
 * @returns Display string such as `12-24-1948 (77 yrs)`
 */
function formatDobWithAge(birthDate: string, age: number | null): string {
  const formatted = formatDisplayDate(birthDate);
  if (!formatted && age == null) return EMPTY_VALUE;
  if (!formatted) return `${age} yrs`;
  return age == null ? formatted : `${formatted} (${age} yrs)`;
}

/**
 * Title-case a demographic sex/gender value.
 *
 * @param value - Raw demographic value
 * @returns Display string
 */
function formatSex(value: string): string {
  if (!value) return EMPTY_VALUE;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Max characters for list-style collapsible strip values (e.g. Care Teams). */
const COLLAPSIBLE_VALUE_MAX_LENGTH = 42;

/**
 * Shorten a single-line display value with an ellipsis when it exceeds the limit.
 *
 * @param text - Raw display string
 * @param maxLength - Maximum visible characters before truncation
 * @returns Truncated string suitable for the patient strip grid
 */
function truncateDisplayText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 1).trimEnd()}…`;
}

/**
 * Join a list into a compact "+N" overflow string.
 *
 * @param values - Display values
 * @returns First item, optionally with a remainder count
 */
function formatListWithOverflow(values: string[]): string {
  if (values.length === 0) return EMPTY_VALUE;
  if (values.length === 1) {
    return truncateDisplayText(values[0], COLLAPSIBLE_VALUE_MAX_LENGTH);
  }
  const summary = `${values[0]} +${values.length - 1}`;
  return truncateDisplayText(summary, COLLAPSIBLE_VALUE_MAX_LENGTH);
}

/**
 * Project the detail payload into header, collapsible strip, and diagnosis cards.
 *
 * @param detail - First-paint eligibility detail response
 * @returns View model consumed by the details header
 */
export function mapEligibilityHeader(
  detail: EligibilityDetailResponse,
): EligibilityHeaderViewModel {
  const { row, patient } = detail;
  const demographics = patient.demographics || {};
  const name = patient.patient_name || row.patient_name || EMPTY_VALUE;
  const birthDate = demographicString(demographics, "birth_date");
  const age = demographicNumber(demographics, "age");
  const sex = formatSex(
    demographicString(demographics, "gender") ||
      demographicString(demographics, "sex"),
  );
  const allergyLabels = (patient.allergies ?? [])
    .map((allergy) =>
      [allergy.substance, allergy.type ? `(${allergy.type})` : ""]
        .filter(Boolean)
        .join(" "),
    )
    .filter(Boolean);
  const careTeam = (patient.care_team || [])
    .map((member) =>
      [member.name, member.role ? `(${member.role})` : ""]
        .filter(Boolean)
        .join(" "),
    )
    .filter(Boolean);
  const vitals = patient.vitals
    ? [patient.vitals.weight, patient.vitals.height, patient.vitals.bsa]
        .filter(Boolean)
        .join(" / ")
    : "";
  const diagnosisCount =
    (patient.cancers?.length || 0) || (patient.diagnoses?.length || 0);

  return {
    initials: patientInitials(name === EMPTY_VALUE ? "" : name),
    name,
    mrn: patient.mrn || row.mrn,
    dobWithAge: formatDobWithAge(birthDate, age),
    sex,
    eligibilityStatus: formatSnakeCaseEnumLabel(
      row.eligibility_status,
      EMPTY_VALUE,
    ),
    eligibilityTier: formatEligibilityTierLabel(
      row.eligibility_tier,
      EMPTY_VALUE,
    ),
    screeningSummary: formatSnakeCaseEnumLabel(
      row.screening?.verdict || row.screening?.status,
      EMPTY_VALUE,
    ),
    patientStatus: row.patient_status || EMPTY_VALUE,
    additionalStudies: row.additional_studies,
    fhirIdentifier:
      patient.patient_fhir_identifier || row.patient_fhir_identifier,
    diagnosis: mapEligibilityDiagnoses(patient),
    collapsibleFields: [
      { label: "DOB", value: formatDisplayDate(birthDate) || EMPTY_VALUE },
      ...(allergyLabels.length > 0
        ? [
            {
              label: "Allergies",
              value: formatListWithOverflow(allergyLabels),
            },
          ]
        : []),
      {
        label: "Diagnoses",
        value: diagnosisCount ? `${diagnosisCount} diagnoses` : EMPTY_VALUE,
      },
      { label: "Care Teams", value: formatListWithOverflow(careTeam) },
      { label: "Upcoming Orders", value: patient.upcoming_order || EMPTY_VALUE },
      { label: "Wt/Ht/BSA", value: vitals || EMPTY_VALUE },
      {
        label: "Next Visit",
        value: formatDisplayDate(patient.next_visit?.start) || EMPTY_VALUE,
      },
      {
        label: "Preferred Phone",
        value: demographicString(demographics, "phone") || EMPTY_VALUE,
      },
      {
        label: "Preferred Language",
        value: demographicString(demographics, "language") || EMPTY_VALUE,
      },
      {
        label: "Pronouns",
        value: demographicString(demographics, "pronouns") || EMPTY_VALUE,
      },
      {
        label: "Next MD Visit",
        value:
          formatDisplayDate(patient.next_md_visit?.start || row.next_md_visit) ||
          EMPTY_VALUE,
      },
      {
        label: "Primary Insurance",
        value: patient.insurance || row.insurance || EMPTY_VALUE,
      },
      {
        label: "Preferred Location",
        value: patient.preferred_location || EMPTY_VALUE,
      },
    ],
  };
}
